import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, Calculator } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PROPERTY_TYPES, typeLabel } from "../lib/utils-app";
import { MortgageCalculator } from "./MortgageCalculator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

export function HeroSearch() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("sale");
  const [q, setQ] = useState("");
  const [type, setType] = useState("any");
  const [beds, setBeds] = useState("any");
  const [showCalc, setShowCalc] = useState(false);

  const go = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (type !== "any") params.set("property_type", type);
    if (beds !== "any") params.set("bedrooms", beds);
    params.set("transaction_type", tab);
    navigate(`/${tab === "rent" ? "rent" : "buy"}?${params.toString()}`);
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-1 mb-3">
        {[["sale", t("search.buy")], ["rent", t("search.rent")]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`label-caps px-6 py-2.5 transition-colors duration-300 ${tab === k ? "bg-primary text-primary-foreground" : "bg-card border border-border text-foreground/60 hover:text-primary hover:border-primary"}`}
            data-testid={`hero-tab-${k}`}>
            {label}
          </button>
        ))}
        <button onClick={() => setShowCalc(true)}
          className="label-caps px-5 py-2.5 flex items-center gap-2 bg-[#d9a441] text-[#1a1205] hover:brightness-105 transition-[filter] duration-300 shadow-[0_6px_20px_-6px_rgba(217,164,65,0.7)]"
          data-testid="hero-mortgage-btn">
          <Calculator size={15} /> {lang === "es" ? "Calculadora Hipotecaria" : "Mortgage Calculator"}
        </button>
      </div>
      <div className="bg-background/90 backdrop-blur-xl border border-border p-2 flex flex-col md:flex-row gap-2">
        <div className="flex items-center gap-2 flex-1 px-3">
          <Search size={18} className="text-primary" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && go()}
            placeholder={t("hero.searchPlaceholder")}
            className="flex-1 bg-transparent py-3 outline-none text-sm"
            data-testid="hero-search-input"
          />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="md:w-40 rounded-none border-border h-auto" data-testid="hero-type-select">
            <SelectValue placeholder={t("search.type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t("common.any")}</SelectItem>
            {PROPERTY_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{typeLabel(pt, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={beds} onValueChange={setBeds}>
          <SelectTrigger className="md:w-32 rounded-none border-border h-auto" data-testid="hero-beds-select">
            <SelectValue placeholder={t("search.beds")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t("common.any")}</SelectItem>
            {[1, 2, 3, 4, 5].map((b) => <SelectItem key={b} value={String(b)}>{b}+ {t("common.bed")}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={go} className="px-8 py-3 bg-primary text-primary-foreground label-caps hover:brightness-110 transition-[filter] duration-300 flex items-center justify-center gap-2" data-testid="hero-search-btn">
          <Search size={16} /> {t("common.search")}
        </button>
      </div>
      {showCalc && <MortgageCalculator onClose={() => setShowCalc(false)} />}
    </div>
  );
}
